'use client'

import { AnimatedAISearchInput } from '@/src/components/common/home/AISearch/AnimatedAISearchInput'
import { useSRPAIFilterManager } from '@/src/hooks/srp/aiRecommendation/useSRPAIFilterManager'
import { useGetSearchParamsObj } from '@/src/hooks/srp/searchParams'
import useSearchResults from '@/src/hooks/srp/useSearchResults'
import { useDraggable } from '@/src/hooks/useDraggable'
import {
    closeSRPFloatingAIChatOnboarding,
    selectSRPFloatingAIChatOnboardingOpen,
    setSRPFloatingAIChatEnabled,
    setSRPFloatingAIChatOnboardingOpen
} from '@/src/redux/slices/aiFeatureSlice'
import { getSelectedCurrency } from '@/src/redux/slices/currencySlice'
import { getRecommendedFilter } from '@/src/redux/slices/srpStatesSlice'
import { useAppDispatch, useAppSelector } from '@/src/redux/store'
import type { AIRecommendationSuggestionsResponse, AISortPreference, SuggestedFilters } from '@/src/types/aiRecommendation'
import type { HomeAIChatMessage, HomeAISearchQuery, HomeAISearchResponse } from '@/src/types/homeAISearch'
import type { SRPAIAssistantResponse, SRPAIManagerActionType, SRPAIManagerRequest } from '@/src/types/srpAIManager'
import { getConversionRateDetails, getCurrencyByLocale } from '@/src/utils/currency/formatCurrency'
import { isEmpty } from '@/src/utils/generalUtils'
import { homeAIMessages, resolveHomeAILocale } from '@/src/utils/homeAISearch/locale'
import { activeFiltersToSuggestedFilters } from '@/src/utils/srp/aiRecommendation/activeFiltersToSuggestedFilters'
import { smartSRPMessages } from '@/src/utils/srp/aiRecommendation/locale'
import { AutoAwesome, Close, CloseFullscreen, DragIndicator, Info } from '@mui/icons-material'
import {
    Box,
    Chip,
    IconButton,
    Tooltip,
    Typography,
    alpha,
    useMediaQuery,
    useTheme
} from '@mui/material'
import { useLocale } from 'next-intl'
import { useRouter } from 'next/navigation'
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { SRPFloatingAIChatOnboardingDialog } from './SRPFloatingAIChatOnboardingDialog'
import { SRPFloatingAIChatOnboardingDrawer } from './SRPFloatingAIChatOnboardingDrawer'

const MAX_CONTEXT_HISTORY = 80
const FLOATING_CHAT_POSITION_STORAGE_KEY = 'srp-floating-ai-chat-position'
const MINIMIZE_TRANSITION_MS = 280

const floatingAnchorSx = {
    position: 'fixed' as const,
    bottom: { xs: 12, md: 16 },
    left: { xs: '50%', md: 'calc(50% + 152px)', lg: 'calc(50% + 156px)' },
    transformOrigin: 'bottom center',
    zIndex: 1200
}

const getPanelTransitionSx = (visible: boolean, delayMs = 0) => ({
    opacity: visible ? 1 : 0,
    transform: visible ? 'translateX(-50%) scale(1)' : 'translateX(-50%) scale(0.12)',
    visibility: (visible ? 'visible' : 'hidden') as 'visible' | 'hidden',
    pointerEvents: (visible ? 'auto' : 'none') as 'auto' | 'none',
    transition: [
        `transform ${MINIMIZE_TRANSITION_MS}ms cubic-bezier(0.4, 0, 0.2, 1) ${delayMs}ms`,
        `opacity ${MINIMIZE_TRANSITION_MS - 40}ms ease ${delayMs}ms`,
        visible
            ? 'visibility 0s linear 0s'
            : `visibility 0s linear ${MINIMIZE_TRANSITION_MS + delayMs}ms`
    ].join(', ')
})
const defaultSearchQuery: HomeAISearchQuery = {
    from: '',
    to: '',
    origin: '',
    destination: '',
    depart_date: '',
    return_date: '',
    adults: '1',
    childs: '0',
    infants: '0',
    class: 'Economy'
}

const areSearchQueriesEqual = (a: HomeAISearchQuery, b: HomeAISearchQuery) =>
    a.from === b.from &&
    a.to === b.to &&
    a.origin === b.origin &&
    a.destination === b.destination &&
    a.depart_date === b.depart_date &&
    (a.return_date || '') === (b.return_date || '') &&
    a.adults === b.adults &&
    a.childs === b.childs &&
    a.infants === b.infants &&
    a.class === b.class &&
    (a.ai_filter_query || '') === (b.ai_filter_query || '')

const getNextRoutingState = (lastAction: SRPAIManagerActionType, awaitingHomeClarification = false) =>
    ({
        activeFlow: lastAction === 'GENERAL' ? 'NONE' : lastAction,
        lastManagerAction: lastAction,
        awaitingHomeClarification
    }) as const

function getSortLabel (sortPreference: AISortPreference | null | undefined, locale: 'en' | 'ar'): string | null {
    if (!sortPreference) return null
    const labelsEn: Record<AISortPreference, string> = {
        price_up: 'price (lowest first)',
        price_down: 'price (highest first)',
        duration_up: 'duration (shortest first)',
        duration_down: 'duration (longest first)',
        departure_up: 'departure time (earliest first)',
        departure_down: 'departure time (latest first)',
        arrival_up: 'arrival time (earliest first)',
        arrival_down: 'arrival time (latest first)',
        smart: 'smart recommendation order'
    }
    const labelsAr: Record<AISortPreference, string> = {
        price_up: 'السعر (الأقل أولاً)',
        price_down: 'السعر (الأعلى أولاً)',
        duration_up: 'المدة (الأقصر أولاً)',
        duration_down: 'المدة (الأطول أولاً)',
        departure_up: 'وقت المغادرة (الأبكر أولاً)',
        departure_down: 'وقت المغادرة (الأحدث أولاً)',
        arrival_up: 'وقت الوصول (الأبكر أولاً)',
        arrival_down: 'وقت الوصول (الأحدث أولاً)',
        smart: 'الترتيب الذكي'
    }
    return locale === 'ar' ? labelsAr[sortPreference] : labelsEn[sortPreference]
}

function formatTimePreferenceLabel (
    preference: NonNullable<AIRecommendationSuggestionsResponse['preferences']>['departureTime']
): string | null {
    if (!preference?.start || !preference?.end) return null
    return `${preference.start} - ${preference.end}`
}

function buildAppliedFilterParts (
    filters: SuggestedFilters | null | undefined,
    preferences: AIRecommendationSuggestionsResponse['preferences'],
    locale: 'en' | 'ar'
): string[] {
    const parts: string[] = []

    if (filters?.nonStop) parts.push(locale === 'ar' ? 'رحلات مباشرة' : 'non-stop flights')
    if (filters?.stops?.length) parts.push(locale === 'ar' ? `عدد التوقفات (${filters.stops.join(', ')})` : `stops (${filters.stops.join(', ')})`)
    if (filters?.layoverAirportCodes?.length) {
        parts.push(
            locale === 'ar'
                ? `مطار التوقف (${filters.layoverAirportCodes.join(', ')})`
                : `layover airport (${filters.layoverAirportCodes.join(', ')})`
        )
    }
    if (filters?.layoverDurationRange) {
        const min = filters.layoverDurationRange.min
        const max = filters.layoverDurationRange.max
        const value = [min != null ? `min ${min}h` : null, max != null ? `max ${max}h` : null].filter(Boolean).join(', ')
        parts.push(locale === 'ar' ? `مدة التوقف (${value})` : `layover duration (${value})`)
    }
    if (filters?.departureTimeSlots?.length) {
        parts.push(locale === 'ar' ? `وقت المغادرة (${filters.departureTimeSlots.join(', ')})` : `departure time (${filters.departureTimeSlots.join(', ')})`)
    }
    if (filters?.arrivalTimeSlots?.length) {
        parts.push(locale === 'ar' ? `وقت الوصول (${filters.arrivalTimeSlots.join(', ')})` : `arrival time (${filters.arrivalTimeSlots.join(', ')})`)
    }
    if (filters?.airlineCodes?.length) {
        parts.push(locale === 'ar' ? `شركة الطيران (${filters.airlineCodes.join(', ')})` : `airline (${filters.airlineCodes.join(', ')})`)
    }
    if (filters?.priceRange && (filters.priceRange.min != null || filters.priceRange.max != null)) {
        const min = filters.priceRange.min != null ? filters.priceRange.min : '-'
        const max = filters.priceRange.max != null ? filters.priceRange.max : '-'
        parts.push(locale === 'ar' ? `نطاق السعر (${min} - ${max})` : `price range (${min} - ${max})`)
    }
    if (filters?.checkedBaggageIncluded) parts.push(locale === 'ar' ? 'يشمل الأمتعة المشحونة' : 'checked baggage included')
    if (filters?.hideBudgetAirlines) parts.push(locale === 'ar' ? 'إخفاء شركات الطيران الاقتصادية' : 'hide budget airlines')

    const departureTimePreferenceLabel = formatTimePreferenceLabel(preferences?.departureTime)
    if (departureTimePreferenceLabel) {
        parts.push(
            locale === 'ar'
                ? `تفضيل وقت المغادرة (${departureTimePreferenceLabel})`
                : `departure time preference (${departureTimePreferenceLabel})`
        )
    }

    const arrivalTimePreferenceLabel = formatTimePreferenceLabel(preferences?.arrivalTime)
    if (arrivalTimePreferenceLabel) {
        parts.push(
            locale === 'ar'
                ? `تفضيل وقت الوصول (${arrivalTimePreferenceLabel})`
                : `arrival time preference (${arrivalTimePreferenceLabel})`
        )
    }

    return parts
}

function buildRecommendationReply (
    payload: AIRecommendationSuggestionsResponse,
    locale: 'en' | 'ar',
    genericFallback: string
): string {
    const filterParts = buildAppliedFilterParts(payload.suggestedFilters, payload.preferences, locale)
    const sortLabel = getSortLabel(payload.sortPreference, locale)

    if (!filterParts.length && !sortLabel) return genericFallback

    if (locale === 'ar') {
        if (filterParts.length && sortLabel) {
            return `تم تطبيق الفلاتر التالية: ${filterParts.join('، ')}. وتم الترتيب حسب ${sortLabel}.`
        }
        if (filterParts.length) {
            return `تم تطبيق الفلاتر التالية: ${filterParts.join('، ')}.`
        }
        return `تم ترتيب النتائج حسب ${sortLabel}.`
    }

    if (filterParts.length && sortLabel) {
        return `Applied filters: ${filterParts.join(', ')}. Sorted by ${sortLabel}.`
    }
    if (filterParts.length) {
        return `Applied filters: ${filterParts.join(', ')}.`
    }
    return `Sorted by ${sortLabel}.`
}

const SRPFloatingAIChat = () => {
    const dispatch = useAppDispatch()
    const locale = useLocale()
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down('md'))
    const homeAILocale = useMemo(() => resolveHomeAILocale(locale), [locale])
    const homeMessages = useMemo(() => homeAIMessages[homeAILocale], [homeAILocale])
    const srpMessages = useMemo(() => smartSRPMessages[homeAILocale], [homeAILocale])
    const onboardingOpen = useAppSelector(selectSRPFloatingAIChatOnboardingOpen)

    const router = useRouter()
    const { urlQueryObj } = useGetSearchParamsObj()
    const { filterData, selectedJourneyLeg, jsons } = useSearchResults()
    const recommendedFilterForLeg = useAppSelector(state => getRecommendedFilter(state)[selectedJourneyLeg])
    const toCurrency = useAppSelector(getSelectedCurrency)
    const { currency: fromCurrency } = getCurrencyByLocale()
    const currentlyAppliedFilters = useMemo(() => {
        const { currencyList = [] } = jsons || {}
        const conversionRateDetails = isEmpty(currencyList)
            ? { rate: 1, buffer: 0 }
            : getConversionRateDetails(currencyList, fromCurrency, toCurrency)
        const { rate = 1, buffer = 0 } = conversionRateDetails || {}
        const priceConversionFactor = rate + (rate * buffer) / 100

        return activeFiltersToSuggestedFilters({
            filterData,
            leg: selectedJourneyLeg as 0 | 1,
            searchFrom: urlQueryObj?.from,
            searchTo: urlQueryObj?.to,
            recommendedFilter: recommendedFilterForLeg,
            priceConversionFactor
        })
    }, [
        filterData,
        selectedJourneyLeg,
        urlQueryObj?.from,
        urlQueryObj?.to,
        recommendedFilterForLeg,
        jsons,
        fromCurrency,
        toCurrency
    ])
    const { applySmartAiPayload } = useSRPAIFilterManager()
    const [minimized, setMinimized] = useState(false)
    const [loading, setLoading] = useState(false)
    const [inputValue, setInputValue] = useState('')
    const [errorMessage, setErrorMessage] = useState<string | null>(null)
    const [chatMessages, setChatMessages] = useState<HomeAIChatMessage[]>([])
    const [aiHomePartialSearchQuery, setAiHomePartialSearchQuery] = useState<HomeAISearchQuery>(defaultSearchQuery)
    const [managerRoutingState, setManagerRoutingState] = useState(() => getNextRoutingState('RECOMMENDATION', false))
    const chatBottomInset = isMobile ? 12 : 16
    const chatDirection = homeAILocale === 'ar' ? 'rtl' : 'ltr'
    const floatingChatPositionStorageKey = `${FLOATING_CHAT_POSITION_STORAGE_KEY}-${chatDirection}`
    const { elementRef: containerRef, dragHandleProps } = useDraggable({
        storage: 'session',
        storageKey: floatingChatPositionStorageKey,
        excludeInteractiveTargets: false,
        pinBottomOnResize: true,
        bottomInset: chatBottomInset,
        direction: chatDirection
    })
    const chatScrollRef = useRef<HTMLDivElement>(null)
    const urlDerivedSearchQuery = useMemo<HomeAISearchQuery>(
        () => ({
            ...defaultSearchQuery,
            from: urlQueryObj.from || '',
            to: urlQueryObj.to || '',
            origin: urlQueryObj.origin || '',
            destination: urlQueryObj.destination || '',
            depart_date: urlQueryObj.depart_date || '',
            return_date: urlQueryObj.return_date || '',
            adults: urlQueryObj.adults || '1',
            childs: urlQueryObj.childs || '0',
            infants: urlQueryObj.infants || '0',
            class: urlQueryObj.class || 'Economy'
        }),
        [
            urlQueryObj.from,
            urlQueryObj.to,
            urlQueryObj.origin,
            urlQueryObj.destination,
            urlQueryObj.depart_date,
            urlQueryObj.return_date,
            urlQueryObj.adults,
            urlQueryObj.childs,
            urlQueryObj.infants,
            urlQueryObj.class
        ]
    )

    useEffect(() => {
        setAiHomePartialSearchQuery(prev =>
            areSearchQueriesEqual(prev, urlDerivedSearchQuery) ? prev : urlDerivedSearchQuery
        )
    }, [urlDerivedSearchQuery])

    useLayoutEffect(() => {
        const el = chatScrollRef.current
        if (el) {
            el.scrollTop = el.scrollHeight
        }
    }, [chatMessages, loading])

    /** SRP chat search updates must not set `ai_filter_query` — that param is only for home → SRP handoff. */
    const navigateToSrp = (data: NonNullable<HomeAISearchResponse['searchQuery']>, filterQuery: string = '') => {
        const queryParams = new URLSearchParams()
        queryParams.set('from', data.from)
        queryParams.set('to', data.to)
        queryParams.set('adults', data.adults || '1')
        queryParams.set('childs', data.childs || '0')
        queryParams.set('infants', data.infants || '0')
        queryParams.set('class', data.class || 'Economy')
        queryParams.set('depart_date', data.depart_date)
        queryParams.set('return_date', data.return_date || '')
        queryParams.set('ai_filter_query', filterQuery)

        router.push(`/${locale}/flights/results?${queryParams.toString()}`)
    }

    const handleSubmit = async () => {
        const trimmed = inputValue.trim()
        if (!trimmed || loading) return

        const userMessage: HomeAIChatMessage = { role: 'user', content: trimmed }
        const nextHistory = [...chatMessages, userMessage].slice(-MAX_CONTEXT_HISTORY)
        setChatMessages(nextHistory)
        setInputValue('')
        setLoading(true)
        setErrorMessage(null)

        const body: SRPAIManagerRequest = {
            userInput: trimmed,
            locale: homeAILocale,
            context: {
                partialSearchQuery: aiHomePartialSearchQuery,
                chatHistory: nextHistory,
                currentlyAppliedFilters,
                routingState: managerRoutingState
            }
        }

        try {
            const response = await fetch('/api/ai-srp-manager', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body)
            })

            const data = (await response.json()) as SRPAIAssistantResponse
            if (response.status === 429) {
                setErrorMessage(homeMessages.errors.RATE_LIMITED)
                return
            }

            if (!response.ok || data.status !== 'success') {
                setErrorMessage(homeMessages.errors.SOMETHING_WENT_WRONG)
                return
            }

            if (data.action.type === 'RECOMMENDATION_RESULT') {
                applySmartAiPayload(data.action.payload, trimmed)
                setManagerRoutingState(getNextRoutingState('RECOMMENDATION', false))
                const recommendationReply = buildRecommendationReply(
                    data.action.payload,
                    homeAILocale,
                    srpMessages.srpAiAppliedUpdateReply
                )
                setChatMessages(prev => [
                    ...prev,
                    { role: 'assistant', content: recommendationReply }
                ])
                return
            }

            if (data.action.type === 'GENERAL') {
                setManagerRoutingState(getNextRoutingState('GENERAL', false))
                const generalMessage =
                    'message' in data.action.payload
                        ? data.action.payload.message
                        : homeMessages.fallbacks.clarificationDefault
                setChatMessages(prev => [...prev, { role: 'assistant', content: generalMessage }])
                return
            }

            setManagerRoutingState(getNextRoutingState('HOME_SEARCH', false))
            const payload = data.action.payload as HomeAISearchResponse

            if (payload.searchQuery) {
                setAiHomePartialSearchQuery(payload.searchQuery)
            }
            if (payload.status === 'needs_clarification') {
                setManagerRoutingState(getNextRoutingState('HOME_SEARCH', true))
                setChatMessages(prev => [
                    ...prev,
                    { role: 'assistant', content: payload.question || homeMessages.fallbacks.clarificationDefault }
                ])
                return
            }

            if ((payload.status === 'ready' || payload.status === 'confirmed') && payload.searchQuery) {
                setManagerRoutingState(getNextRoutingState('HOME_SEARCH', false))
                navigateToSrp(payload.searchQuery, payload.filterQuery)
                return
            }

            setErrorMessage(homeMessages.errors.SOMETHING_WENT_WRONG)
        } catch {
            setErrorMessage(homeMessages.errors.NETWORK_ERROR)
        } finally {
            setLoading(false)
        }
    }

    const handleContinueOnboarding = () => {
        dispatch(closeSRPFloatingAIChatOnboarding())
    }
    const handleInfoClick = () => {
        dispatch(setSRPFloatingAIChatOnboardingOpen(true))
    }

    const handleExitOnboarding = () => {
        dispatch(setSRPFloatingAIChatEnabled(false))
    }

    const onboardingHeaderHeight = isMobile ? 140 : 180

    const handleToggleChange = () => {
        dispatch(setSRPFloatingAIChatEnabled(false))
    }

    const handleVoiceTranscript = (transcript: string) => {
        setInputValue(prev => (prev ? `${prev} ${transcript}` : transcript))
    }

    return (
        <>
            <Box
                onClick={() => setMinimized(false)}
                aria-label={srpMessages.aiChatModeTitle}
                role="button"
                tabIndex={minimized ? 0 : -1}
                sx={[
                    floatingAnchorSx,
                    getPanelTransitionSx(minimized, minimized ? 80 : 0),
                    theme => ({
                        width: 52,
                        height: 52,
                        borderRadius: '50%',
                        cursor: minimized ? 'pointer' : 'default',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                        boxShadow: 4,
                        animation: minimized ? 'srpFabPulse 1.2s ease-out 0.15s 1' : 'none',
                        '@keyframes srpFabPulse': {
                            '0%': { boxShadow: `0 0 0 0 ${alpha(theme.palette.primary.main, 0.45)}` },
                            '70%': { boxShadow: `0 0 0 10px ${alpha(theme.palette.primary.main, 0)}` },
                            '100%': { boxShadow: `0 0 0 0 ${alpha(theme.palette.primary.main, 0)}` }
                        },
                        ...(minimized && {
                            '&:hover': { transform: 'translateX(-50%) scale(1.1)' }
                        })
                    })
                ]}
            >
                <AutoAwesome
                    sx={{
                        color: '#fff',
                        fontSize: 26,
                        transition: `transform ${MINIMIZE_TRANSITION_MS}ms cubic-bezier(0.34, 1.56, 0.64, 1)`,
                        transform: minimized ? 'scale(1) rotate(0deg)' : 'scale(0) rotate(-90deg)'
                    }}
                />
            </Box>

            <Box
                ref={containerRef}
                lang={homeAILocale === 'ar' ? 'ar' : 'en'}
                dir={chatDirection}
                sx={[
                    floatingAnchorSx,
                    getPanelTransitionSx(!minimized, minimized ? 0 : 60),
                    {
                        overflow: 'hidden',
                        boxSizing: 'border-box',
                        width: { xs: '90%', md: 'min(60%, calc(100vw - 280px - 32px - 96px))' },
                        maxWidth: 650,
                        pt: '3px',
                        borderRadius: 3,
                        background: theme =>
                            `linear-gradient(185deg, ${alpha(theme.palette.primary.main, 1)} 0%, ${alpha(theme.palette.secondary.main, 0.7)} 50%, ${alpha(theme.palette.primary.main, 0.85)} 100%)`,
                        boxShadow: 3,
                        '&[data-dragging]': {
                            touchAction: 'none',
                            userSelect: 'none',
                            '& [data-drag-handle]': { cursor: 'grabbing' }
                        }
                    }
                ]}
            >
                <Box sx={{ bgcolor: 'background.paper', borderRadius: 2.85, p: 2, pt: 1 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            px: 0.75,
                            mb: 0.5,
                            touchAction: 'none'
                        }}
                    >
                        <Box display="flex" alignItems="center" gap={1}>
                            <Box display={"flex"} alignItems={"center"}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                    {srpMessages.aiChatModeTitle}
                                </Typography>
                                <IconButton onClick={handleInfoClick} size='small' sx={{ fontSize: "18px" }} color='primary'><Info fontSize='inherit' /></IconButton>
                            </Box>


                            <Chip
                                label={srpMessages.floatingAIOnboarding.betaLabel}
                                size="small"
                                color="primary"
                                variant="outlined"
                            />

                        </Box>
                        <Tooltip
                            title={srpMessages.floatingAIDragHandleTooltip}
                            arrow={false}
                            placement="top"
                        >
                            <Box
                                component="span"
                                {...dragHandleProps}
                                data-drag-handle
                                role="button"
                                tabIndex={0}
                                aria-label={srpMessages.floatingAIDragHandleAria}
                                sx={{
                                    cursor: 'grab',
                                    // border: 1,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    // borderColor: 'primary.main',
                                    // borderRadius: 0.5,
                                    p: 0.25,
                                    touchAction: 'none',
                                    transform: 'rotate(90deg)'
                                }}
                            >
                                <DragIndicator color="primary" />
                            </Box>
                        </Tooltip>

                        <Box display="flex" alignItems="center" gap={1} >
                            <IconButton
                                onClick={() => setMinimized(true)}
                                size='small'
                                color='primary'
                                aria-label="Minimize"
                                sx={{
                                    transition: 'transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1)',
                                    '&:hover': { transform: 'scale(1.15)' },
                                    '&:active': { transform: 'scale(0.88)' }
                                }}
                            >
                                <CloseFullscreen fontSize='inherit' color='primary' />
                            </IconButton>

                            <IconButton
                                onClick={handleToggleChange}
                                size='small'
                                color='primary'
                            >
                                <Close color='primary' />
                            </IconButton>
                        </Box>





                    </Box>

                    <>
                        {!!chatMessages.length && (
                            <Box
                                ref={chatScrollRef}
                                sx={{
                                    mb: 1,
                                    maxHeight: 130,
                                    overflowY: 'auto',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 0.75,
                                    scrollbarWidth: 'none',
                                    msOverflowStyle: 'none',
                                    '&::-webkit-scrollbar': {
                                        display: 'none'
                                    }
                                }}
                            >
                                {chatMessages.map((message, index) => (
                                    <Box
                                        key={`${message.role}-${index}`}
                                        sx={{
                                            alignSelf: message.role === 'user' ? 'end' : 'start',
                                            maxWidth: '88%',
                                            px: 1.25,
                                            py: 0.75,
                                            borderRadius: 2,
                                            bgcolor: message.role === 'user' ? 'primary.main' : 'grey.100',
                                            color: message.role === 'user' ? 'primary.contrastText' : 'text.primary'
                                        }}
                                    >
                                        <Typography variant="body2">{message.content}</Typography>
                                    </Box>
                                ))}
                                {loading && (
                                    <Box
                                        sx={{
                                            alignSelf: 'start',
                                            maxWidth: '88%',
                                            px: 1.25,
                                            py: 0.75,
                                            borderRadius: 2,
                                            bgcolor: 'grey.100',
                                            color: 'text.primary',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5
                                        }}
                                    >
                                        {[0, 1, 2].map(index => (
                                            <Box
                                                key={`srp-typing-dot-${index}`}
                                                sx={{
                                                    width: 7,
                                                    height: 7,
                                                    borderRadius: '50%',
                                                    bgcolor: 'text.secondary',
                                                    opacity: 0.35,
                                                    animation: 'aiTypingWave 1s ease-in-out infinite',
                                                    animationDelay: `${index * 0.15}s`,
                                                    '@keyframes aiTypingWave': {
                                                        '0%, 80%, 100%': {
                                                            transform: 'translateY(0)',
                                                            opacity: 0.3
                                                        },
                                                        '40%': {
                                                            transform: 'translateY(-3px)',
                                                            opacity: 1
                                                        }
                                                    }
                                                }}
                                            />
                                        ))}
                                    </Box>
                                )}
                            </Box>
                        )}

                        <Box sx={{ borderRadius: 999, p: 0.75 }}>
                            <AnimatedAISearchInput
                                isMobile={true} //for srp we want the compact design
                                showPlaceHolder
                                messages={homeMessages}
                                placeholderHints={srpMessages.smartSearchPlaceHolderHints}
                                startIconSize={18}
                                submitIconSize={18}
                                loadingIndicatorSize={16}
                                submitButtonSize={{ mobile: 30, desktop: 36 }}
                                inputValue={inputValue}
                                loading={loading}
                                locale={locale}
                                onInputChange={setInputValue}
                                onVoiceTranscript={handleVoiceTranscript}
                                onSubmit={handleSubmit}
                            />
                        </Box>
                        {/* <Box ml={3}>
                            <Typography variant="caption" color='textDisabled' mt={1} lineHeight={0}>
                                {homeMessages.description}
                            </Typography>
                        </Box> */}


                        {errorMessage && (
                            <Typography variant="body2" color="error" sx={{ mt: 0.75, ml: 1.5 }}>
                                {errorMessage}
                            </Typography>
                        )}
                    </>
                </Box>
            </Box>

            {!isMobile && (
                <SRPFloatingAIChatOnboardingDialog
                    open={onboardingOpen}
                    onboardingHeaderHeight={onboardingHeaderHeight}
                    onboardingCopy={srpMessages.floatingAIOnboarding}
                    aiChatModeTitle={srpMessages.aiChatModeTitle}
                    onContinue={handleContinueOnboarding}
                    onExit={handleExitOnboarding}
                />
            )
            }

            {
                isMobile && (
                    <SRPFloatingAIChatOnboardingDrawer
                        open={onboardingOpen}
                        onboardingHeaderHeight={onboardingHeaderHeight}
                        onboardingCopy={srpMessages.floatingAIOnboarding}
                        aiChatModeTitle={srpMessages.aiChatModeTitle}
                        onContinue={handleContinueOnboarding}
                        onExit={handleExitOnboarding}
                    />
                )
            }
        </>
    )
}

export default SRPFloatingAIChat
